import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Visibility, VisibilityOff, Email, Lock, ArrowBack } from "@mui/icons-material";
import { useSnackbar } from "notistack";

import {
  loginUser,
  selectAuthLoading,
  selectAuthError,
  clearError,
} from "../../redux/slices/authSlice";
import api from "../../api/axiosInstance";
import AuthMethodChooser from "../../components/auth/AuthMethodChooser";

const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const loading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);

  // "Continue with Email" / "Continue with Google" / "Continue with Apple"
  // are offered as three equal options up front — this just reveals the
  // existing email+password form in place of the chooser; Google/Apple
  // complete sign-in right from the chooser itself.
  const [showEmailForm, setShowEmailForm] = useState(false);

  const [form, setForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);

  const handleChange = (e) => {
    dispatch(clearError());
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await dispatch(loginUser(form));
    if (loginUser.fulfilled.match(result)) {
      const role = result.payload.user?.role;
      const roleRoutes = { admin: "/admin", rider: "/rider" };
      navigate(roleRoutes[role] || "/dashboard", { replace: true });
    }
  };

  const handleResendVerification = async () => {
    if (!resendEmail) {
      enqueueSnackbar("Please enter your email", { variant: "warning" });
      return;
    }
    setResendLoading(true);
    try {
      await api.post("/auth/resend-verification", { email: resendEmail });
      enqueueSnackbar("Verification email sent! Check your inbox.", {
        variant: "success",
      });
      setResendOpen(false);
      setResendEmail("");
    } catch (err) {
      enqueueSnackbar(
        err.response?.data?.message || "Failed to resend email",
        { variant: "error" }
      );
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h3" fontWeight={700} color="text.primary">
        Welcome back
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Sign in to your Waypoint Imports account
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => dispatch(clearError())}
        >
          {error}
        </Alert>
      )}

      {!showEmailForm ? (
        <AuthMethodChooser onContinueWithEmail={() => setShowEmailForm(true)} />
      ) : (
        <>
          <Button
            type="button"
            onClick={() => setShowEmailForm(false)}
            startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
            sx={{
              color: "text.secondary",
              textTransform: "none",
              fontSize: 13,
              alignSelf: "flex-start",
              mb: 1.5,
              px: 0,
              "&:hover": { backgroundColor: "transparent", color: "primary.main" },
            }}
          >
            Use a different method
          </Button>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            <TextField
          label="Email address"
          name="email"
          type="email"
          value={form.email}
          onChange={handleChange}
          required
          autoComplete="email"
          autoFocus
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Email sx={{ fontSize: 18, color: "text.disabled" }} />
                </InputAdornment>
              ),
            },
          }}
        />

        <TextField
          label="Password"
          name="password"
          type={showPassword ? "text" : "password"}
          value={form.password}
          onChange={handleChange}
          required
          autoComplete="current-password"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ fontSize: 18, color: "text.disabled" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowPassword((p) => !p)}
                    edge="end"
                  >
                    {showPassword ? (
                      <VisibilityOff sx={{ fontSize: 18 }} />
                    ) : (
                      <Visibility sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            mt: -0.5,
            fontSize: 13,
          }}
        >
          <Typography
            component={Link}
            to="/forgot-password"
            sx={{
              color: "primary.main",
              textDecoration: "none",
              fontSize: 13,
              textTransform: "none",
              padding: "8px 0",
              minWidth: "auto",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Forgot password?
          </Typography>
          <Button
            type="button"
            onClick={() => setResendOpen(true)}
            sx={{
              color: "primary.main",
              textDecoration: "none",
              fontSize: 13,
              textTransform: "none",
              padding: "8px 0",
              minWidth: "auto",
              "&:hover": { textDecoration: "underline", backgroundColor: "inherit" },
            }}
          >
            Resend verification?
          </Button>
        </Box>

        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disabled={loading}
          sx={{ mt: 0.5, py: 1.375, fontSize: 14, fontWeight: 600 }}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: "white" }} />
          ) : (
            "Sign In"
          )}
        </Button>
          </Box>
        </>
      )}

      <Divider sx={{ my: 3 }}>
        <Typography variant="caption" color="text.disabled">
          OR
        </Typography>
      </Divider>

      <Button
        component={Link}
        to="/register"
        variant="outlined"
        size="large"
        fullWidth
        sx={{ py: 1.375, fontSize: 14, fontWeight: 600 }}
      >
        Create Account
      </Button>

      <Dialog open={resendOpen} onClose={() => setResendOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Resend Verification Email</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Enter your email address to receive a new verification link.
          </Typography>
          <TextField
            fullWidth
            label="Email address"
            type="email"
            value={resendEmail}
            onChange={(e) => setResendEmail(e.target.value)}
            placeholder="your@email.com"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button type="button" onClick={() => setResendOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleResendVerification}
            variant="contained"
            disabled={resendLoading || !resendEmail}
          >
            {resendLoading ? <CircularProgress size={20} /> : "Resend Email"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoginPage;
